import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { ProspectDetail } from "../../../../types/onboarding";
import {
  awaitingSignature,
  canSendContract,
  contractStatusLabel,
  hasContractHistory,
  isEsignAwaited,
  isTerminal,
  sendActionFor,
} from "../../../../utils/onboardingStates";
import { signMethodLabel } from "../../../../constants/contractSigning";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import { contractTimestamps } from "../../../../utils/contractTimestamps";
import UploadSignedDialog from "./UploadSignedDialog";
import ConfirmSignedDialog from "./ConfirmSignedDialog";
import DocumentReference from "./DocumentReference";
import EmailPreviewDialog from "./EmailPreviewDialog";
import { sendContractErrorMessage } from "./contractErrors";

interface ContractPanelProps {
  prospect: ProspectDetail;
  detail: {
    sendContract: () => Promise<boolean>;
    markEsignSent: () => Promise<boolean>;
    sendHubLink: () => Promise<boolean>;
    uploadSigned: (pdf: File) => Promise<boolean>;
    confirmSigned: (signedContractUrl: string) => Promise<boolean>;
  };
  // Set when the next-step button in the header asks for this panel's action.
  requestedAction?: NextStepAction | null;
  onRequestHandled?: () => void;
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatDuration = (hours?: number | null): string => {
  if (hours === null || hours === undefined) return "—";
  // Round the total first so the remainder can never render as "24 h".
  const total = Math.round(hours);
  if (total < 24) return `${total} h`;
  const days = Math.floor(total / 24);
  const rem = total % 24;
  return rem ? `${days} d ${rem} h` : `${days} d`;
};

/**
 * A stored contract document. Rendering — link or copyable text — is decided by
 * whether the address actually opens (see DocumentReference, FR-013).
 */
const DocumentRow: React.FC<{ label: string; reference?: string | null }> = ({
  label,
  reference,
}) =>
  reference ? (
    <div className="col-12 md:col-6 mb-2">
      <div className="text-500 text-sm">{label}</div>
      <div className="text-sm">
        <DocumentReference reference={reference} />
      </div>
    </div>
  ) : null;

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="col-12 md:col-6 mb-2">
      <div className="text-500 text-sm">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  ) : null;

/**
 * Contract-signing panel on the prospect detail page (US1: send by method).
 * Shown from CONTRACT_DRAFT_CREATED through CONTRACT_SIGNED. At the draft state
 * it offers exactly one send action, chosen by the prospect's signature method;
 * once a signature method is not set, it prompts ops to set one first (FR-015).
 * Record actions (upload/confirm signed) are added in US2.
 */
type OpenDialog = "none" | "upload" | "confirm";

const ContractPanel: React.FC<ContractPanelProps> = ({
  prospect,
  detail,
  requestedAction = null,
  onRequestHandled,
}) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<OpenDialog>("none");
  // Both email sends are confirmed in front of the email itself (CWA-173); they
  // used to go out on a single click. Marking e-sign as sent emails nobody.
  const [emailOpen, setEmailOpen] = useState(false);
  const close = () => setOpen("none");

  const state = prospect.current_state;
  const method = prospect.sign_method;
  const sendAction = sendActionFor(method);

  // Overdue is an authoritative backend flag (ProspectResponse.contract_overdue,
  // FR-019).
  const isOverdue = prospect.contract_overdue === true;

  // The contract record's timestamps survive only within an action response, so
  // these fall back to the prospect's recorded transitions.
  const { sentAt, signedAt, timeToSignHours } = contractTimestamps(prospect);

  const run = async (fn: () => Promise<boolean>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // The header's next-step button hands over to this panel rather than carrying
  // the action itself: the decision belongs in front of the contract's own
  // dates and documents. Runs once per request, then hands the request back.
  useEffect(() => {
    if (!requestedAction) return;
    if (requestedAction === "send-contract") {
      if (sendAction === "send" || sendAction === "hub-sign") setEmailOpen(true);
      if (sendAction === "esign-sent") run(detail.markEsignSent);
    }
    if (requestedAction === "record-signed") {
      if (state === "CONTRACT_SENT" && method === "MANUAL") setOpen("upload");
      if (isEsignAwaited(state) && method === "DIGITAL") setOpen("confirm");
    }
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  const renderSendAction = () => {
    if (!canSendContract(state)) return null;

    // No signature method captured (e.g. legacy prospect) — cannot pick an
    // action safely; direct ops to set it first (FR-015).
    if (!method || !sendAction) {
      return (
        <Message
          severity="info"
          className="w-full"
          text="Set a signature method (via Edit) before sending the contract."
        />
      );
    }

    if (sendAction === "send") {
      return (
        <Button
          label="Send contract"
          icon="pi pi-send"
          loading={busy}
          disabled={busy}
          onClick={() => setEmailOpen(true)}
        />
      );
    }
    if (sendAction === "esign-sent") {
      return (
        <Button
          label="Mark e-sign as sent"
          icon="pi pi-check-circle"
          loading={busy}
          disabled={busy}
          onClick={() => run(detail.markEsignSent)}
        />
      );
    }
    // hub-sign
    return (
      <Button
        label="Send signing link via email"
        icon="pi pi-link"
        loading={busy}
        disabled={busy}
        onClick={() => setEmailOpen(true)}
      />
    );
  };

  const renderRecordAction = () => {
    // Manual: upload the countersigned PDF at CONTRACT_SENT.
    if (state === "CONTRACT_SENT" && method === "MANUAL") {
      return (
        <Button
          label="Upload signed contract"
          icon="pi pi-upload"
          onClick={() => setOpen("upload")}
        />
      );
    }
    // Digital: confirm the stored signed document at ESIGN_AWAITED.
    if (isEsignAwaited(state) && method === "DIGITAL") {
      return (
        <Button
          label="Confirm signed"
          icon="pi pi-check"
          onClick={() => setOpen("confirm")}
        />
      );
    }
    // Click-to-sign: the customer signs in the Hub — no ops record action.
    if (state === "CONTRACT_SENT" && method === "CLICK_TO_SIGN") {
      return (
        <Message
          severity="info"
          className="w-full"
          text="Awaiting the customer's signature via the Hub link."
        />
      );
    }
    return null;
  };

  // Shown for the whole journey after the draft, not just while signing is in
  // progress: the signed contract still has to be checkable after go-live. The
  // send/record actions above gate on their own exact states, so none of them
  // reappear here. Placed after the hooks so none of them run conditionally.
  if (isTerminal(state) || !hasContractHistory(state)) return null;

  return (
    <div
      className="mb-4 onboarding-section"
      data-testid="contract-panel"
    >
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h3 className="m-0">Contract</h3>
        <div className="flex align-items-center gap-2">
          <Tag value={contractStatusLabel(state)} />
          {isOverdue && awaitingSignature(state) && (
            <Tag severity="danger" value="Overdue" />
          )}
          {method && (
            <span className="text-500 text-sm">{signMethodLabel(method)}</span>
          )}
        </div>
      </div>

      <div className="mb-2">{renderSendAction()}</div>
      <div className="mb-2">{renderRecordAction()}</div>

      <div className="grid">
        {/* The draft belongs with the contract's other documents as well as in
            the prospect's own fields: an agent working the contract should not
            have to leave the tab to open what was drafted. */}
        <DocumentRow
          label="Contract draft"
          reference={prospect.contract_draft_url}
        />
        <InfoRow label="Sent on" value={formatDate(sentAt)} />
        <DocumentRow
          label="Sent document"
          reference={prospect.unsigned_pdf_url}
        />
        <DocumentRow
          label="E-signature document"
          reference={prospect.esign_document_url}
        />
        <DocumentRow
          label="Signed document"
          reference={prospect.signed_pdf_url}
        />
        <InfoRow label="Signed on" value={formatDate(signedAt)} />
        <InfoRow
          label="Time to sign"
          value={
            timeToSignHours != null ? formatDuration(timeToSignHours) : undefined
          }
        />
      </div>

      <UploadSignedDialog
        visible={open === "upload"}
        onHide={close}
        onSubmit={detail.uploadSigned}
      />
      <ConfirmSignedDialog
        visible={open === "confirm"}
        onHide={close}
        onSubmit={detail.confirmSigned}
      />
      {(sendAction === "send" || sendAction === "hub-sign") && (
        <EmailPreviewDialog
          visible={emailOpen}
          prospectId={prospect.id}
          type={sendAction === "send" ? "CONTRACT" : "HUB_SIGNING"}
          header={
            sendAction === "send" ? "Send contract" : "Send signing link via email"
          }
          intro={
            sendAction === "send"
              ? `The contract will be emailed to ${prospect.email}, with the agreement attached as a PDF.`
              : `${prospect.email} will be emailed a link to review and sign the contract in the Hub.`
          }
          sendLabel="Send"
          sendIcon={sendAction === "send" ? "pi pi-send" : "pi pi-link"}
          onSend={() =>
            run(sendAction === "send" ? detail.sendContract : detail.sendHubLink)
          }
          onHide={() => setEmailOpen(false)}
          errorMessage={sendContractErrorMessage}
        />
      )}
    </div>
  );
};

export default ContractPanel;
